(() => {
  'use strict';

  const setupBundlePicker = (picker) => {
    if (picker.dataset.psnReady === 'true') return;
    picker.dataset.psnReady = 'true';

    const radios = Array.from(picker.querySelectorAll('input[name="quantity"]'));
    if (radios.length === 0) return;

    const productSection = picker.closest('.section-product-info') || picker.closest('section');
    const formId = picker.dataset.formId || '';
    const sectionId = picker.dataset.sectionId || formId.replace(/^product-form-/, '');
    const mainForm = formId ? document.getElementById(formId) : null;
    const stickyForm = sectionId ? document.getElementById(`product-form-sticky-${sectionId}`) : null;

    const syncFormQuantity = (form, quantity) => {
      if (!form) return;
      const quantityControls = Array.from(form.elements).filter((control) => control.name === 'quantity');
      quantityControls.forEach((control) => {
        if (control.type === 'radio') {
          control.checked = control.value === quantity;
        } else {
          control.value = quantity;
          control.setAttribute('value', quantity);
        }
      });
    };

    const syncPrice = (displayTotal) => {
      if (!displayTotal || !sectionId) return;
      const escapedSectionId = CSS.escape(sectionId);
      document.querySelectorAll(
        `#x-atc-button-${escapedSectionId} .main-product-price > p.price, ` +
        `#x-atc-button-${escapedSectionId} .main-product-price > .price-sale, ` +
        `.price-sticky-${escapedSectionId} > .price > p.price, ` +
        `.price-sticky-${escapedSectionId} > .price > .price-sale, ` +
        `[data-psn-sticky-display-total]`
      ).forEach((price) => {
        price.textContent = displayTotal;
      });
    };

    const render = () => {
      const selected = radios.find((radio) => radio.checked) || radios[0];
      const quantity = selected.value === '2' ? '2' : '1';
      syncFormQuantity(mainForm, quantity);
      syncFormQuantity(stickyForm, quantity);
      syncPrice(selected.dataset.displayTotal);

      picker.querySelectorAll('.psn-bundle__option').forEach((option) => {
        const input = option.querySelector('input[name="quantity"]');
        option.classList.toggle('is-active', Boolean(input?.checked));
      });
    };

    picker.addEventListener('change', render);
    productSection?.addEventListener('submit', render, true);
    if (sectionId) {
      document.addEventListener(`eurus:product-page-variant-select:updated:${sectionId}`, () => {
        requestAnimationFrame(render);
      });
    }
    render();
  };

  const setupVideoGallery = (gallery) => {
    if (gallery.dataset.psnReady === 'true') return;
    gallery.dataset.psnReady = 'true';

    const video = gallery.querySelector('[data-psn-video]');
    const playButton = gallery.querySelector('[data-psn-video-play]');
    const options = Array.from(gallery.querySelectorAll('[data-psn-video-option]'));
    if (!video || !playButton || options.length === 0) return;

    let activeOption = options.find((option) => option.getAttribute('aria-pressed') === 'true') || options[0];

    const selectVideo = (option) => {
      if (!option || option === activeOption) return;
      video.pause();
      activeOption = option;
      video.src = option.dataset.videoSrc || '';
      video.poster = option.dataset.videoPoster || '';
      video.setAttribute('aria-label', option.dataset.videoLabel || 'PeeSport video');
      video.load();
      playButton.hidden = false;
      playButton.setAttribute('aria-label', `Play ${(option.dataset.videoLabel || 'PeeSport video').toLowerCase()}`);
      options.forEach((candidate) => {
        candidate.setAttribute('aria-pressed', String(candidate === option));
      });
    };

    options.forEach((option) => {
      option.addEventListener('click', () => selectVideo(option));
    });

    playButton.addEventListener('click', async () => {
      try {
        await video.play();
      } catch (error) {
        playButton.hidden = false;
      }
    });

    video.addEventListener('play', () => {
      playButton.hidden = true;
    });
    video.addEventListener('pause', () => {
      if (video.currentTime === 0 || video.ended) playButton.hidden = false;
    });
    video.addEventListener('ended', () => {
      playButton.hidden = false;
    });
  };

  const setupStickyVisibility = (productSection) => {
    if (!productSection || productSection.dataset.psnStickyReady === 'true') return;

    const mainButton = productSection.querySelector('.x-block-buy-buttons .add_to_cart_button');
    const sticky = productSection.querySelector('[data-psn-sticky-managed]');
    if (!mainButton || !sticky) return;
    productSection.dataset.psnStickyReady = 'true';

    const viewport = window.visualViewport;
    let frame = 0;
    let visible = false;

    const setVisible = (nextVisible) => {
      if (visible === nextVisible && sticky.hidden === !nextVisible) return;
      visible = nextVisible;
      sticky.hidden = !nextVisible;
      sticky.setAttribute('aria-hidden', String(!nextVisible));
    };

    const update = () => {
      frame = 0;
      const viewportTop = viewport?.offsetTop || 0;
      const viewportHeight = viewport?.height || window.innerHeight;
      const viewportBottom = viewportTop + viewportHeight;
      const buttonRect = mainButton.getBoundingClientRect();
      if (buttonRect.width === 0 || buttonRect.height === 0) {
        setVisible(false);
        return;
      }
      const footerRect = document.getElementById('section-footer')?.getBoundingClientRect();
      const footerReached = Boolean(footerRect && footerRect.top < viewportBottom - 8);
      const comfortablyVisible = buttonRect.top >= viewportTop + 10 && buttonRect.bottom <= viewportBottom - 10;
      const clipped = buttonRect.top < viewportTop + 2 || buttonRect.bottom > viewportBottom - 2;

      if (footerReached) {
        setVisible(false);
      } else if (visible) {
        setVisible(!comfortablyVisible);
      } else {
        setVisible(clipped);
      }
    };

    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    viewport?.addEventListener('scroll', schedule, { passive: true });
    viewport?.addEventListener('resize', schedule, { passive: true });

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(schedule);
      resizeObserver.observe(mainButton);
    }

    schedule();
  };

  const initialize = (root = document) => {
    root.querySelectorAll('ps-bundle-picker').forEach(setupBundlePicker);
    root.querySelectorAll('ps-video-gallery').forEach(setupVideoGallery);
    root.querySelectorAll('.section-product-info').forEach((productSection) => {
      setupStickyVisibility(productSection);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));

})();
